// src/screens/RotationPlanner.tsx
// Planificateur de rotation : pour chaque période, 5 lignes (postes) sur une
// frise de temps. On glisse (ou on touche puis on place) un joueur sur une ligne
// pour lui attribuer du temps ; les bords des segments se redimensionnent.
// Sauvegarde dans public.rotation_plans.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation as useTranslationOrig } from "react-i18next";
import {
  AlertCircle,
  ArrowLeft,
  BarChart2,
  Check,
  CheckCircle2,
  LayoutList,
  Redo2,
  Save,
  Settings2,
  Trash2,
  Undo2,
  UserPlus,
  X,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import {
  COURT_SIZE,
  emptyPlan,
  formatSeconds,
  isValidPlan,
  newId,
  normalizePlayers,
  PlanData,
  PlanPlayer,
  PlanSegment,
  PLAYER_COLORS,
  RPlayer,
} from "../lib/rotations";
import { Card, IconBtn, Modal, SquadModal, TR } from "../components/rotations/RotationUI";

type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => { t: RawT })(ns);

const SNAP = 30; // secondes
const GUARD_KEY = "rotationPlannerGuard";
const BACK = "__back__";

const snap = (sec: number, dur: number) => Math.min(dur, Math.max(0, Math.round(sec / SNAP) * SNAP));

type ResizeDrag = {
  segId: string;
  edge: "start" | "end";
  neighborId: string | null; // segment collé qui bouge avec la frontière
  rect: DOMRect;
  dur: number;
  before: PlanData;
};

export const RotationPlanner = () => {
  const { plan_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t: tRaw } = useTranslationSafe("rotations");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => String(tRaw(key, options) ?? ""),
    [tRaw]
  );

  const isNew = !plan_id || plan_id === "new";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(isNew ? null : plan_id!);
  const [teamId, setTeamId] = useState<string | null>(searchParams.get("team"));
  const [teamRoster, setTeamRoster] = useState<RPlayer[]>([]);
  const [name, setName] = useState("");
  const [data, setData] = useState<PlanData>(() => emptyPlan());
  const [past, setPast] = useState<PlanData[]>([]);
  const [future, setFuture] = useState<PlanData[]>([]);
  const [tab, setTab] = useState<"editor" | "analysis">("editor");
  const [armed, setArmed] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [selectedSeg, setSelectedSeg] = useState<string | null>(null);
  const [showSquad, setShowSquad] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [savedSnapshot, setSavedSnapshot] = useState<string>("");

  const dataRef = useRef(data);
  dataRef.current = data;
  const resizeRef = useRef<ResizeDrag | null>(null);

  // ---------- Chargement ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let tId = searchParams.get("team");
      let loaded: PlanData | null = null;
      let loadedName = "";

      if (!isNew) {
        const { data: row, error } = await supabase
          .from("rotation_plans")
          .select("*")
          .eq("id", plan_id)
          .single();
        if (cancelled) return;
        if (error || !row) {
          console.error("[RotationPlanner] load error:", error);
          setLoadError(tr("planner.loadError"));
          setLoading(false);
          return;
        }
        tId = row.team_id;
        loadedName = row.name ?? "";
        loaded = isValidPlan(row.data) ? row.data : emptyPlan();
      }

      let roster: RPlayer[] = [];
      if (tId) {
        const { data: team } = await supabase.from("teams").select("players").eq("id", tId).maybeSingle();
        roster = normalizePlayers(team?.players);
      }
      if (cancelled) return;

      const initial = loaded ?? emptyPlan(roster);
      setTeamId(tId);
      setTeamRoster(roster);
      setName(loadedName);
      setData(initial);
      setSavedSnapshot(JSON.stringify({ name: loadedName, data: initial }));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan_id]);

  const dirty = !loading && !loadError && JSON.stringify({ name, data }) !== savedSnapshot;

  // ---------- Historique ----------
  const commit = useCallback((updater: (prev: PlanData) => PlanData) => {
    const cur = dataRef.current;
    const next = updater(cur);
    if (next === cur) return;
    setPast((p) => [...p.slice(-79), cur]);
    setFuture([]);
    setData(next);
  }, []);

  const undo = () => {
    if (!past.length) return;
    setFuture((f) => [...f, data]);
    setData(past[past.length - 1]);
    setPast((p) => p.slice(0, -1));
    setSelectedSeg(null);
  };

  const redo = () => {
    if (!future.length) return;
    setPast((p) => [...p, data]);
    setData(future[future.length - 1]);
    setFuture((f) => f.slice(0, -1));
    setSelectedSeg(null);
  };

  // ---------- Dérivés ----------
  const dur = data.settings.periodMinutes * 60;
  const periods = useMemo(
    () => Array.from({ length: data.settings.periods }, (_, i) => i + 1),
    [data.settings.periods]
  );

  const colorOf = useMemo(() => {
    const map = new Map<number, string>();
    data.players.forEach((p, i) => map.set(p.number, PLAYER_COLORS[i % PLAYER_COLORS.length]));
    return (num: number) => map.get(num) ?? "#9ca3af";
  }, [data.players]);

  const playerByNum = useMemo(() => {
    const map = new Map<number, PlanPlayer>();
    data.players.forEach((p) => map.set(p.number, p));
    return map;
  }, [data.players]);

  const labelOf = useCallback(
    (num: number, short = false) => {
      const p = playerByNum.get(num);
      if (!p) return `#${num}`;
      const name = short ? p.lastName || p.firstName : [p.firstName, p.lastName].filter(Boolean).join(" ");
      return name ? `#${num} ${name}` : `#${num}`;
    },
    [playerByNum]
  );

  const slotLabels = useMemo(() => ["pg", "sg", "sf", "pf", "c"].map((k) => tr(`positions.${k}`)), [tr]);

  const periodTitle = (p: number) =>
    data.settings.periods === 4 ? tr("planner.quarter", { n: p }) : tr("planner.periodN", { n: p });

  // Temps attribué par joueur / période
  const playerTime = useMemo(() => {
    const map = new Map<number, Record<number, number>>();
    data.segments.forEach((s) => {
      const byP = map.get(s.player) ?? {};
      byP[s.period] = (byP[s.period] || 0) + (s.end - s.start);
      map.set(s.player, byP);
    });
    return map;
  }, [data.segments]);

  const totalOf = (num: number) => Object.values(playerTime.get(num) || {}).reduce((a, b) => a + b, 0);

  // Conflits : un joueur sur deux postes en même temps
  const conflicts = useMemo(() => {
    const ids = new Set<string>();
    const list: { player: number; period: number; start: number; end: number }[] = [];
    periods.forEach((period) => {
      const segs = data.segments.filter((s) => s.period === period);
      for (let i = 0; i < segs.length; i++) {
        for (let j = i + 1; j < segs.length; j++) {
          const a = segs[i];
          const b = segs[j];
          if (a.player !== b.player) continue;
          const start = Math.max(a.start, b.start);
          const end = Math.min(a.end, b.end);
          if (end > start) {
            ids.add(a.id);
            ids.add(b.id);
            list.push({ player: a.player, period, start, end });
          }
        }
      }
    });
    return { ids, list };
  }, [data.segments, periods]);

  const assigned = data.segments.reduce((a, s) => a + (s.end - s.start), 0);
  const target = dur * COURT_SIZE * data.settings.periods;
  const isValid = assigned === target && conflicts.list.length === 0;

  const activePlayer = armed ?? dragging;
  const activePositions = activePlayer != null ? playerByNum.get(activePlayer)?.positions ?? [] : [];

  // ---------- Placement ----------
  const placePlayer = (period: number, lane: number, player: number, rawT: number) => {
    commit((prev) => {
      const d = prev.settings.periodMinutes * 60;
      const t = snap(rawT, d);
      const laneSegs = prev.segments
        .filter((s) => s.period === period && s.lane === lane)
        .sort((a, b) => a.start - b.start);
      const hit = laneSegs.find((s) => s.start <= rawT && rawT < s.end);

      if (hit) {
        if (hit.player === player) return prev;
        // Près du début du segment : on remplace le joueur ; sinon on coupe.
        if (t - hit.start < SNAP) {
          return {
            ...prev,
            segments: prev.segments.map((s) => (s.id === hit.id ? { ...s, player } : s)),
          };
        }
        const cut = Math.min(t, hit.end - SNAP);
        return {
          ...prev,
          segments: [
            ...prev.segments.map((s) => (s.id === hit.id ? { ...s, end: cut } : s)),
            { id: newId(), period, lane, player, start: cut, end: hit.end },
          ],
        };
      }

      // Case vide : on remplit tout le trou autour du point visé.
      const prevEnd = Math.max(0, ...laneSegs.filter((s) => s.end <= rawT).map((s) => s.end));
      const nextStart = Math.min(d, ...laneSegs.filter((s) => s.start >= rawT).map((s) => s.start));
      if (nextStart - prevEnd <= 0) return prev;
      return {
        ...prev,
        segments: [...prev.segments, { id: newId(), period, lane, player, start: prevEnd, end: nextStart }],
      };
    });
  };

  const timeFromEvent = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * dur;
  };

  const onLaneClick = (e: React.MouseEvent<HTMLDivElement>, period: number, lane: number) => {
    const t = timeFromEvent(e.clientX, e.currentTarget);
    if (armed != null) {
      placePlayer(period, lane, armed, t);
      return;
    }
    const segEl = (e.target as HTMLElement).closest("[data-seg]") as HTMLElement | null;
    setSelectedSeg(segEl ? segEl.dataset.seg ?? null : null);
  };

  const onLaneDrop = (e: React.DragEvent<HTMLDivElement>, period: number, lane: number) => {
    e.preventDefault();
    const num = Number(e.dataTransfer.getData("text/plain"));
    setDragging(null);
    if (Number.isNaN(num) || !playerByNum.has(num)) return;
    placePlayer(period, lane, num, timeFromEvent(e.clientX, e.currentTarget));
  };

  const deleteSegment = (id: string) => {
    commit((prev) => ({ ...prev, segments: prev.segments.filter((s) => s.id !== id) }));
    setSelectedSeg(null);
  };

  // ---------- Redimensionnement ----------
  const onHandleDown = (e: React.PointerEvent<HTMLDivElement>, seg: PlanSegment, edge: "start" | "end") => {
    e.stopPropagation();
    e.preventDefault();
    const laneEl = e.currentTarget.closest("[data-lane]") as HTMLElement | null;
    if (!laneEl) return;
    const neighbor = dataRef.current.segments.find(
      (s) =>
        s.period === seg.period &&
        s.lane === seg.lane &&
        s.id !== seg.id &&
        (edge === "start" ? s.end === seg.start : s.start === seg.end)
    );
    resizeRef.current = {
      segId: seg.id,
      edge,
      neighborId: neighbor?.id ?? null,
      rect: laneEl.getBoundingClientRect(),
      dur,
      before: dataRef.current,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelectedSeg(seg.id);
  };

  const onHandleMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = resizeRef.current;
    if (!drag) return;
    const ratio = Math.min(1, Math.max(0, (e.clientX - drag.rect.left) / drag.rect.width));
    const t = snap(ratio * drag.dur, drag.dur);
    setData((prev) => {
      const seg = prev.segments.find((s) => s.id === drag.segId);
      if (!seg) return prev;
      const laneSegs = prev.segments.filter(
        (s) => s.period === seg.period && s.lane === seg.lane && s.id !== seg.id
      );
      const neighbor = drag.neighborId ? prev.segments.find((s) => s.id === drag.neighborId) : undefined;
      let min: number;
      let max: number;
      if (drag.edge === "start") {
        max = seg.end - SNAP;
        min = neighbor
          ? neighbor.start + SNAP
          : Math.max(0, ...laneSegs.filter((s) => s.end <= seg.start).map((s) => s.end));
      } else {
        min = seg.start + SNAP;
        max = neighbor
          ? neighbor.end - SNAP
          : Math.min(drag.dur, ...laneSegs.filter((s) => s.start >= seg.end).map((s) => s.start));
      }
      const v = Math.min(max, Math.max(min, t));
      if (v === seg[drag.edge]) return prev;
      return {
        ...prev,
        segments: prev.segments.map((s) => {
          if (s.id === seg.id) return { ...s, [drag.edge]: v };
          if (neighbor && s.id === neighbor.id) return { ...s, [drag.edge === "start" ? "end" : "start"]: v };
          return s;
        }),
      };
    });
  };

  const onHandleUp = () => {
    const drag = resizeRef.current;
    resizeRef.current = null;
    if (drag && drag.before !== dataRef.current) {
      setPast((p) => [...p.slice(-79), drag.before]);
      setFuture([]);
    }
  };

  // ---------- Clavier ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") {
        setArmed(null);
        setSelectedSeg(null);
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedSeg) {
        e.preventDefault();
        deleteSegment(selectedSeg);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeg]);

  // ---------- Joueurs ----------
  const togglePosition = (num: number, lane: number) =>
    commit((prev) => ({
      ...prev,
      players: prev.players.map((p) =>
        p.number !== num
          ? p
          : {
              ...p,
              positions: p.positions.includes(lane)
                ? p.positions.filter((x) => x !== lane)
                : [...p.positions, lane].sort(),
            }
      ),
    }));

  const removePlayer = (num: number) => {
    commit((prev) => ({
      ...prev,
      players: prev.players.filter((p) => p.number !== num),
      segments: prev.segments.filter((s) => s.player !== num),
    }));
    if (armed === num) setArmed(null);
  };

  const applySquad = (squad: RPlayer[]) => {
    commit((prev) => {
      const keep = new Set(squad.map((p) => p.number));
      const existing = new Map(prev.players.map((p) => [p.number, p]));
      return {
        ...prev,
        players: squad.map((p) => existing.get(p.number) ?? { ...p, positions: [] }),
        segments: prev.segments.filter((s) => keep.has(s.player)),
      };
    });
    setShowSquad(false);
  };

  const applySettings = (settings: PlanData["settings"]) => {
    commit((prev) => {
      const d = settings.periodMinutes * 60;
      return {
        ...prev,
        settings,
        segments: prev.segments
          .filter((s) => s.period <= settings.periods && s.start < d)
          .map((s) => ({ ...s, end: Math.min(s.end, d) })),
      };
    });
    setShowSettings(false);
  };

  // ---------- Sauvegarde ----------
  // `redirect: false` quand on enregistre juste avant de quitter la page.
  const save = async ({ redirect = true } = {}): Promise<boolean> => {
    setSaveStatus("saving");
    const finalName = name.trim() || tr("planner.untitled");
    let error: any = null;
    let createdId: string | null = null;

    if (planId) {
      ({ error } = await supabase
        .from("rotation_plans")
        .update({ name: finalName, data, updated_at: new Date().toISOString() })
        .eq("id", planId));
    } else {
      const { data: userData } = await supabase.auth.getUser();
      const res = await supabase
        .from("rotation_plans")
        .insert({ name: finalName, data, team_id: teamId, user_id: userData?.user?.id })
        .select("id")
        .single();
      error = res.error;
      createdId = res.data?.id ?? null;
    }

    window.setTimeout(() => setSaveStatus("idle"), 2500);
    if (error) {
      console.error("[RotationPlanner] save error:", error);
      setSaveStatus("error");
      return false;
    }
    setName(finalName);
    setSavedSnapshot(JSON.stringify({ name: finalName, data }));
    setSaveStatus("saved");
    if (createdId) {
      setPlanId(createdId);
      if (redirect) {
        const path = `/rotations/plan/${createdId}`;
        if (guardActiveRef.current && window.history.state?.[GUARD_KEY]) {
          // On retire d'abord l'entrée "garde" pour que l'URL remplacée soit la bonne.
          guardActiveRef.current = false;
          afterGuardPopRef.current = () => navigate(path, { replace: true });
          window.history.back();
        } else {
          navigate(path, { replace: true });
        }
      }
    }
    return true;
  };

  // ---------- Garde "modifications non enregistrées" ----------
  // BrowserRouter ne permet pas useBlocker : on combine beforeunload (fermeture /
  // rafraîchissement), interception des liens internes, et une entrée d'historique
  // "garde" pour le bouton précédent du navigateur / d'Android.
  const [pendingLeave, setPendingLeave] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const guardActiveRef = useRef(false);
  const afterGuardPopRef = useRef<(() => void) | null>(null);
  const bypassRef = useRef(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypassRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (bypassRef.current) return;
    if (dirty && !guardActiveRef.current) {
      window.history.pushState({ ...window.history.state, [GUARD_KEY]: true }, "", window.location.href);
      guardActiveRef.current = true;
    } else if (!dirty && guardActiveRef.current && window.history.state?.[GUARD_KEY]) {
      guardActiveRef.current = false;
      afterGuardPopRef.current = () => {};
      window.history.back();
    }
  }, [dirty]);

  useEffect(() => {
    const onPop = () => {
      if (bypassRef.current) return;
      if (afterGuardPopRef.current) {
        const cb = afterGuardPopRef.current;
        afterGuardPopRef.current = null;
        cb();
        return;
      }
      if (!guardActiveRef.current) return;
      // L'entrée "garde" vient d'être dépilée.
      if (dirtyRef.current) {
        window.history.pushState({ ...window.history.state, [GUARD_KEY]: true }, "", window.location.href);
        setPendingLeave(BACK);
      } else {
        guardActiveRef.current = false;
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || bypassRef.current || e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== "_self")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const path = url.pathname + url.search + url.hash;
      if (path === window.location.pathname + window.location.search + window.location.hash) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingLeave(path);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const requestLeave = (path: string) => {
    if (dirty) setPendingLeave(path);
    else navigate(path);
  };

  const leave = (target: string) => {
    bypassRef.current = true;
    setPendingLeave(null);
    if (target === BACK) {
      // entrée garde + page du planificateur
      window.history.go(guardActiveRef.current ? -2 : -1);
    } else {
      navigate(target);
    }
  };

  const saveAndLeave = async (target: string) => {
    setLeaving(true);
    bypassRef.current = true; // la garde reste en place : leave() la saute
    const ok = await save({ redirect: false });
    setLeaving(false);
    if (ok) leave(target);
    else bypassRef.current = false;
  };

  // ---------- Rendu ----------
  if (loading) {
    return <section className="min-h-[60vh] flex items-center justify-center text-gray-500">{tr("loading")}</section>;
  }
  if (loadError) {
    return (
      <section className="max-w-lg mx-auto px-4 py-10 text-center">
        <p className="text-red-600 mb-4">{loadError}</p>
        <button onClick={() => navigate("/rotations")} className="px-4 py-2 rounded-xl border text-sm">
          {tr("actions.back")}
        </button>
      </section>
    );
  }

  const ticks = Array.from({ length: data.settings.periodMinutes }, (_, i) => i * 60);

  return (
    <section
      className="min-h-[100dvh] w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-50/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-[1500px] mx-auto px-3 py-2 flex flex-wrap items-center gap-2">
          <button
            onClick={() => requestLeave("/rotations")}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/60"
            aria-label={tr("actions.back")}
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline text-sm font-medium">{tr("actions.back")}</span>
          </button>

          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1">
            <button
              onClick={() => setTab("editor")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium ${
                tab === "editor" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <LayoutList size={16} /> {tr("planner.editor")}
            </button>
            <button
              onClick={() => setTab("analysis")}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium ${
                tab === "analysis" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <BarChart2 size={16} /> {tr("tabs.analysis")}
            </button>
          </div>

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tr("planner.untitled")}
            className="order-last sm:order-none basis-full sm:basis-auto flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-center font-semibold"
          />

          <div className="ml-auto flex items-center gap-1">
            <IconBtn onClick={undo} disabled={!past.length} label={tr("actions.undo")}>
              <Undo2 size={18} />
            </IconBtn>
            <IconBtn onClick={redo} disabled={!future.length} label={tr("actions.redo")}>
              <Redo2 size={18} />
            </IconBtn>
            <IconBtn onClick={() => setShowSettings(true)} label={tr("actions.settings")}>
              <Settings2 size={18} />
            </IconBtn>
            <button
              onClick={() => save()}
              disabled={saveStatus === "saving"}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold ${
                saveStatus === "error"
                  ? "bg-red-600 text-white"
                  : saveStatus === "saved"
                  ? "bg-green-600 text-white"
                  : "bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black"
              }`}
            >
              {saveStatus === "saved" ? <Check size={16} /> : <Save size={16} />}
              <span className="hidden sm:inline">
                {saveStatus === "saving"
                  ? tr("save.saving")
                  : saveStatus === "saved"
                  ? tr("save.saved")
                  : saveStatus === "error"
                  ? tr("save.error")
                  : tr("save.label")}
              </span>
              {dirty && saveStatus === "idle" && <span className="w-2 h-2 rounded-full bg-orange-500" />}
            </button>
          </div>
        </div>

        {/* Bandeau joueurs compact (mobile) : toucher pour sélectionner puis toucher une ligne */}
        {tab === "editor" && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-800 px-3 py-2 flex gap-2 overflow-x-auto">
            {data.players.map((p) => (
              <button
                key={p.number}
                onClick={() => setArmed((cur) => (cur === p.number ? null : p.number))}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold text-white ${
                  armed === p.number ? "ring-2 ring-offset-2 ring-gray-900 dark:ring-white dark:ring-offset-gray-950" : ""
                }`}
                style={{ backgroundColor: colorOf(p.number) }}
              >
                {labelOf(p.number, true)} · {formatSeconds(totalOf(p.number))}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-[1500px] mx-auto px-3 pt-4 pb-24">
        {tab === "editor" ? (
          <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
            <div className="min-w-0">
              {armed != null && (
                <div className="mb-3 flex items-center justify-between gap-2 rounded-xl bg-gray-900 text-white dark:bg-white dark:text-gray-900 px-3 py-2 text-sm">
                  <span>{tr("planner.armedHint", { player: labelOf(armed) })}</span>
                  <button onClick={() => setArmed(null)} aria-label={tr("actions.cancel")}>
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="grid gap-x-6 gap-y-6 2xl:grid-cols-2">
                {periods.map((period) => (
                  <div key={period} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-3 sm:p-4">
                    <h2 className="text-center font-bold mb-2">{periodTitle(period)}</h2>
                    {/* Axe du temps (temps restant) */}
                    <div className="flex">
                      <div className="w-10 shrink-0" />
                      <div className="relative flex-1 h-5">
                        {ticks.map((t) => (
                          <span
                            key={t}
                            className="absolute -translate-x-1/2 text-[10px] tabular-nums text-gray-500"
                            style={{ left: `${(t / dur) * 100}%` }}
                          >
                            {formatSeconds(dur - t)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {slotLabels.map((label, lane) => {
                        const segs = data.segments.filter((s) => s.period === period && s.lane === lane);
                        const dim = activePlayer != null && activePositions.length > 0 && !activePositions.includes(lane);
                        return (
                          <div key={lane} className="flex items-center">
                            <span className="w-10 shrink-0 text-xs font-bold text-gray-600 dark:text-gray-300">{label}</span>
                            <div
                              data-lane
                              onClick={(e) => onLaneClick(e, period, lane)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => onLaneDrop(e, period, lane)}
                              className={`relative flex-1 h-9 rounded-md bg-gray-100 dark:bg-gray-800 transition-opacity ${
                                armed != null ? "cursor-copy" : ""
                              } ${dim ? "opacity-40" : ""}`}
                            >
                              {/* repères minute */}
                              {ticks.slice(1).map((t) => (
                                <span
                                  key={t}
                                  className="absolute top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
                                  style={{ left: `${(t / dur) * 100}%` }}
                                />
                              ))}
                              {segs.length === 0 && (
                                <span className="absolute inset-0 flex items-center justify-center text-[11px] text-gray-400 pointer-events-none text-center px-2">
                                  {tr("planner.dropHint")}
                                </span>
                              )}
                              {segs.map((s) => {
                                const conflict = conflicts.ids.has(s.id);
                                const selected = selectedSeg === s.id;
                                return (
                                  <div
                                    key={s.id}
                                    data-seg={s.id}
                                    title={`${labelOf(s.player)} · ${formatSeconds(dur - s.start)} → ${formatSeconds(dur - s.end)}`}
                                    className={`absolute top-0.5 bottom-0.5 rounded-md text-white text-[11px] font-semibold flex items-center overflow-hidden select-none ${
                                      selected ? "ring-2 ring-gray-900 dark:ring-white z-10" : ""
                                    } ${conflict ? "outline outline-2 outline-red-600" : ""}`}
                                    style={{
                                      left: `${(s.start / dur) * 100}%`,
                                      width: `${((s.end - s.start) / dur) * 100}%`,
                                      backgroundColor: colorOf(s.player),
                                      backgroundImage: conflict
                                        ? "repeating-linear-gradient(45deg, rgba(0,0,0,.18) 0 6px, transparent 6px 12px)"
                                        : undefined,
                                    }}
                                  >
                                    <div
                                      onPointerDown={(e) => onHandleDown(e, s, "start")}
                                      onPointerMove={onHandleMove}
                                      onPointerUp={onHandleUp}
                                      onClick={(e) => e.stopPropagation()}
                                      className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize touch-none hover:bg-black/20"
                                    />
                                    <span className="truncate px-3">{labelOf(s.player, true)}</span>
                                    {selected && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteSegment(s.id);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded bg-black/30 p-0.5"
                                        aria-label={tr("planner.deleteSegment")}
                                      >
                                        <X size={12} />
                                      </button>
                                    )}
                                    <div
                                      onPointerDown={(e) => onHandleDown(e, s, "end")}
                                      onPointerMove={onHandleMove}
                                      onPointerUp={onHandleUp}
                                      onClick={(e) => e.stopPropagation()}
                                      className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize touch-none hover:bg-black/20"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Validation */}
              <div
                className={`mt-5 rounded-2xl p-4 border ${
                  isValid
                    ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
                    : "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold mb-1">
                  {isValid ? (
                    <CheckCircle2 size={18} className="text-green-600" />
                  ) : (
                    <AlertCircle size={18} className="text-red-600" />
                  )}
                  <span className={isValid ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}>
                    {isValid ? tr("planner.valid") : tr("planner.invalid")}
                  </span>
                </div>
                <p className="text-sm">
                  {tr("planner.playingTime", { done: formatSeconds(assigned), total: formatSeconds(target) })}
                </p>
                {conflicts.list.length > 0 && (
                  <ul className="mt-2 text-sm text-red-700 dark:text-red-400 list-disc pl-5">
                    {conflicts.list.map((c, i) => (
                      <li key={i}>
                        {tr("planner.conflict", {
                          player: labelOf(c.player),
                          period: periodTitle(c.period),
                          from: formatSeconds(dur - c.start),
                          to: formatSeconds(dur - c.end),
                        })}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-xs text-gray-500">{tr("planner.help")}</p>
              </div>
            </div>

            {/* Cartes joueurs */}
            <div className="space-y-3 lg:order-first lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto lg:pr-1">
              <button
                onClick={() => setShowSquad(true)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-orange-400 text-orange-700 dark:text-orange-300 px-3 py-2 text-sm font-semibold"
              >
                <UserPlus size={16} /> {tr("planner.players")}
              </button>
              {data.players.length === 0 && (
                <p className="text-sm text-gray-500 italic">{tr("planner.noPlayers")}</p>
              )}
              {data.players.map((p) => (
                <div
                  key={p.number}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", String(p.number));
                    setDragging(p.number);
                  }}
                  onDragEnd={() => setDragging(null)}
                  className={`rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm border cursor-grab ${
                    armed === p.number ? "border-gray-900 dark:border-white ring-2 ring-gray-900 dark:ring-white" : "border-gray-200 dark:border-gray-800"
                  }`}
                >
                  <button
                    onClick={() => setArmed((cur) => (cur === p.number ? null : p.number))}
                    className="w-full flex items-center justify-between px-3 py-2 text-white font-semibold text-left"
                    style={{ backgroundColor: colorOf(p.number) }}
                  >
                    <span className="truncate">{labelOf(p.number)}</span>
                    <span className="tabular-nums">{formatSeconds(totalOf(p.number))}</span>
                  </button>
                  <div className="flex items-center gap-1 px-3 py-2">
                    {slotLabels.map((label, lane) => (
                      <button
                        key={lane}
                        onClick={() => togglePosition(p.number, lane)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          p.positions.includes(lane)
                            ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900"
                            : "bg-gray-100 text-gray-400 dark:bg-gray-800"
                        }`}
                        title={tr("planner.positionPref")}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      onClick={() => removePlayer(p.number)}
                      className="ml-auto p-1 text-red-600"
                      aria-label={tr("planner.removePlayer")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <PlanAnalysis
            data={data}
            periods={periods}
            labelOf={labelOf}
            colorOf={colorOf}
            playerTime={playerTime}
            tr={tr}
          />
        )}
      </div>

      {showSquad && (
        <SquadModal
          current={data.players}
          roster={teamRoster}
          onCourt={[]}
          onClose={() => setShowSquad(false)}
          onSave={applySquad}
          tr={tr}
        />
      )}
      {pendingLeave != null && (
        <Modal
          title={tr("planner.unsavedTitle")}
          onClose={() => setPendingLeave(null)}
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setPendingLeave(null)}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm"
              >
                {tr("planner.stay")}
              </button>
              <button
                onClick={() => leave(pendingLeave)}
                className="px-3 py-2 rounded-xl border border-red-300 text-red-600 text-sm font-semibold"
              >
                {tr("planner.leaveWithoutSaving")}
              </button>
              <button
                onClick={() => saveAndLeave(pendingLeave)}
                disabled={leaving}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                <Save size={16} /> {leaving ? tr("save.saving") : tr("planner.saveAndLeave")}
              </button>
            </div>
          }
        >
          <p className="text-sm">{tr("planner.unsavedBody")}</p>
          {saveStatus === "error" && <p className="text-sm text-red-600 mt-2">{tr("planner.saveFailed")}</p>}
        </Modal>
      )}

      {showSettings && (
        <PlanSettingsModal
          settings={data.settings}
          onClose={() => setShowSettings(false)}
          onSave={applySettings}
          tr={tr}
        />
      )}
    </section>
  );
};

const PlanSettingsModal = ({
  settings,
  onClose,
  onSave,
  tr,
}: {
  settings: PlanData["settings"];
  onClose: () => void;
  onSave: (s: PlanData["settings"]) => void;
  tr: TR;
}) => {
  const [draft, setDraft] = useState(settings);
  const field = (key: keyof PlanData["settings"], label: string, min: number, max: number) => (
    <label className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={draft[key]}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          setDraft((d) => ({ ...d, [key]: Number.isNaN(v) ? min : Math.min(max, Math.max(min, v)) }));
        }}
        className="w-20 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-right"
      />
    </label>
  );
  return (
    <Modal
      title={tr("settings.title")}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm">
            {tr("actions.cancel")}
          </button>
          <button onClick={() => onSave(draft)} className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold">
            {tr("actions.validate")}
          </button>
        </>
      }
    >
      {field("periods", tr("settings.periods"), 1, 8)}
      {field("periodMinutes", tr("settings.periodMinutes"), 1, 30)}
      <p className="text-xs text-gray-500 mt-2">{tr("planner.settingsHint")}</p>
    </Modal>
  );
};

const PlanAnalysis = ({
  data,
  periods,
  labelOf,
  colorOf,
  playerTime,
  tr,
}: {
  data: PlanData;
  periods: number[];
  labelOf: (n: number) => string;
  colorOf: (n: number) => string;
  playerTime: Map<number, Record<number, number>>;
  tr: TR;
}) => {
  const rows = data.players
    .map((p) => {
      const byP = playerTime.get(p.number) || {};
      return { p, byP, total: Object.values(byP).reduce((a, b) => a + b, 0) };
    })
    .sort((a, b) => b.total - a.total);
  const max = Math.max(1, ...rows.map((r) => r.total));
  const dur = data.settings.periodMinutes * 60;

  // Couverture de chaque poste (temps attribué / temps total)
  const coverage = Array.from({ length: COURT_SIZE }, (_, lane) =>
    data.segments.filter((s) => s.lane === lane).reduce((a, s) => a + (s.end - s.start), 0)
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <Card title={tr("analysis.plannedTitle")}>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{tr("planner.noPlayers")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
                  <th className="py-2 pr-3">{tr("analysis.player")}</th>
                  <th className="py-2 pr-3 min-w-[160px]">{tr("analysis.total")}</th>
                  {periods.map((p) => (
                    <th key={p} className="py-2 px-2 text-right">
                      {tr("period.short", { n: p })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ p, byP, total }) => (
                  <tr key={p.number} className="border-b border-gray-100 dark:border-gray-800/60">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: colorOf(p.number) }} />
                      {labelOf(p.number)}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                          <div className="h-full" style={{ width: `${(total / max) * 100}%`, backgroundColor: colorOf(p.number) }} />
                        </div>
                        <span className="tabular-nums font-semibold w-12 text-right">{formatSeconds(total)}</span>
                      </div>
                    </td>
                    {periods.map((per) => (
                      <td key={per} className="py-2 px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                        {byP[per] ? formatSeconds(byP[per]) : "–"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card title={tr("analysis.coverageTitle")}>
        <ul className="space-y-3">
          {["pg", "sg", "sf", "pf", "c"].map((k, lane) => {
            const pct = coverage[lane] / (dur * data.settings.periods);
            return (
              <li key={k}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold">{tr(`positions.${k}`)}</span>
                  <span className="tabular-nums text-gray-500">{Math.round(pct * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div className={`h-full ${pct >= 1 ? "bg-green-500" : "bg-orange-500"}`} style={{ width: `${pct * 100}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
};

export default RotationPlanner;
